import { Link } from 'react-router-dom';
import { ArrowRight, Timer, ShieldCheck, Leaf } from '@phosphor-icons/react';
import { HERO } from '@/constants/testIds';

export default function Hero() {
  return (
    <section
      data-testid="hero-section"
      className="relative overflow-hidden bg-cream-100 bg-grain border-b border-stone-200/60"
    >
      {/* Decorative blurred shapes - earthy */}
      <div className="pointer-events-none absolute -top-24 -right-24 w-96 h-96 rounded-full bg-terracotta-100/60 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-32 -left-16 w-96 h-96 rounded-full bg-olive-100/70 blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-20 lg:py-24 grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
        <div className="space-y-6 animate-fade-up">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/80 backdrop-blur border border-olive-200/70 text-xs font-medium text-olive-800">
            <span className="w-1.5 h-1.5 rounded-full bg-terracotta-600 animate-pulse" />
            Faridabad · Ballabgarh · COD Available
          </span>

          <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[1.02] text-stone-900">
            <span className="block">Fresh Grocery &</span>
            <span className="block italic text-accent-gradient">Daily Essentials</span>
            <span className="block text-brand-700">Delivered to Your Doorstep.</span>
          </h1>
          <p className="text-base sm:text-lg text-stone-600 max-w-xl leading-relaxed">
            Shop groceries, snacks, beverages, household essentials and daily-use products from MyZippyKart. Currently delivering across Faridabad, Ballabgarh and nearby serviceable areas based on PIN code, distance and product availability. Expansion to more cities is planned.
          </p>

          <div className="flex flex-wrap items-center gap-3 pt-2">
            <Link
              to="/shop"
              data-testid={HERO.shopNowBtn}
              className="inline-flex items-center gap-2 h-12 px-7 rounded-full bg-accent-gradient text-white font-bold tracking-wide shadow-cta hover:brightness-110 hover:scale-[1.02] active:scale-95 transition-all"
            >
              Shop Now <ArrowRight size={18} weight="bold" />
            </Link>
            <a
              href="#categories"
              data-testid={HERO.exploreCategoriesBtn}
              className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-white/90 backdrop-blur border-2 border-brand-500 text-brand-700 font-semibold hover:bg-brand-50 hover:border-brand-600 transition-colors"
            >
              Browse Categories
            </a>
          </div>

          <div className="grid grid-cols-3 gap-3 sm:gap-6 pt-6 max-w-xl">
            <Feature icon={Timer} title="Flexible" sub="By area & availability" />
            <Feature icon={Leaf} title="Fresh & Quality" sub="Hand-picked daily" />
            <Feature icon={ShieldCheck} title="COD" sub="Pay on delivery" />
          </div>
        </div>

        <div className="relative">
          <div className="grid grid-cols-6 grid-rows-6 gap-3 h-[440px] sm:h-[520px]">
            <div className="col-span-4 row-span-4 rounded-3xl overflow-hidden bg-stone-200 shadow-xl shadow-olive-900/10 ring-1 ring-olive-200/50">
              <img src="https://images.unsplash.com/photo-1542838132-92c53300491e?auto=format&fit=crop&w=900&q=80" alt="Fresh produce" className="w-full h-full object-cover" />
            </div>
            <div className="col-span-2 row-span-3 rounded-3xl overflow-hidden bg-olive-100 ring-1 ring-olive-200/50">
              <img src="https://images.unsplash.com/photo-1610348725531-843dff563e2c?auto=format&fit=crop&w=600&q=80" alt="Fresh vegetables" className="w-full h-full object-cover" />
            </div>
            <div className="col-span-2 row-span-3 rounded-3xl overflow-hidden bg-terracotta-100 ring-1 ring-terracotta-200/50">
              <img src="https://images.unsplash.com/photo-1550583724-b2692b85b150?auto=format&fit=crop&w=600&q=80" alt="Dairy" className="w-full h-full object-cover" />
            </div>
            <div className="col-span-4 row-span-2 rounded-3xl overflow-hidden bg-stone-200 relative ring-1 ring-stone-200/50">
              <img src="https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?auto=format&fit=crop&w=900&q=80" alt="Delivery" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-r from-olive-900/75 via-olive-900/40 to-transparent" />
              <div className="absolute inset-0 flex items-center px-6">
                <div className="text-white">
                  <p className="text-xs uppercase tracking-widest text-terracotta-300">Aaj ka vaada</p>
                  <p className="font-heading text-xl sm:text-2xl mt-1">Fresh aaya. Fresh gaya.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Feature({ icon: Icon, title, sub }) {
  return (
    <div className="flex flex-col items-start gap-1">
      <div className="w-10 h-10 rounded-xl bg-white grid place-items-center text-terracotta-700 ring-1 ring-olive-200/60 shadow-sm">
        <Icon size={18} weight="duotone" />
      </div>
      <p className="text-sm font-semibold text-stone-900 mt-1">{title}</p>
      <p className="text-xs text-stone-500">{sub}</p>
    </div>
  );
}
